import { useMutation, useQuery } from "@tanstack/react-query";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getUser, postIncentivesRecordReferralSignupStarted } from "@/api/methods";
import { useTranslation } from "@/core/i18n";
import { storage } from "@/modules/storage";
import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { LocationListPickerV3 } from "../components/LocationListPickerV3/LocationListPickerV3";
import OnboardingHeaderV3 from "../components/OnboardingScreenHeader/OnboardingHeaderV3";
import { PracticeLocation, transformLocationsByCity } from "../utils/locationTransform";
import type { SignUpLocationSuggestion } from "../utils/suggestSignUpLocation";

interface LocationPickerScreenProps {
  locations?: PracticeLocation[];
  onLocationSelect?: (locationId: string) => void | Promise<void>;
  selectedLocation?: string;
  /** Preselects the patient's nearby city or branch until they choose one themselves. */
  suggestion?: SignUpLocationSuggestion | null;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  isLoading?: boolean;
  loadingMessage?: string;
  hideFindNearby?: boolean;
  onFindPracticeClick?: () => void;
}

export function LocationPickerScreen({
  locations = [],
  onLocationSelect,
  selectedLocation = "",
  suggestion,
  className,
  prefix,
  suffix,
  isLoading = false,
  loadingMessage,
  hideFindNearby = false,
  onFindPracticeClick,
}: Readonly<LocationPickerScreenProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSelection, setCurrentSelection] = useState<string[]>(
    selectedLocation ? [selectedLocation] : [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  // Transform locations into city groups for V3 component
  const cityGroups = useMemo(() => transformLocationsByCity(locations), [locations]);

  const suggestedCity = suggestion
    ? cityGroups.find((group) =>
        group.locations.some((location) => location.id === suggestion.locationId),
      )?.city
    : undefined;
  const suggestedLocationId =
    suggestedCity && suggestion?.scope === "location" ? suggestion.locationId : undefined;
  // The suggestion arrives after the picker mounts, so a new key re-seeds it. Once the patient
  // picks a city or branch the key is frozen, so a late suggestion never replaces their choice.
  const suggestionPickerKey = `${suggestedCity ?? ""}:${suggestedLocationId ?? ""}`;
  const [chosenPickerKey, setChosenPickerKey] = useState<string | null>(null);
  const hasChosen = chosenPickerKey !== null;
  const locationIdToContinue =
    hasChosen || currentSelection.length > 0 ? currentSelection[0] : suggestedLocationId;

  const handleSelectionChange = (selectedIds: string[]) => {
    setChosenPickerKey((key) => key ?? suggestionPickerKey);
    setCurrentSelection(selectedIds);
    // Pass the selectedIds directly to avoid race condition with state updates
    if (selectedIds.length > 0) {
      handleContinue(selectedIds[0]);
    }
  };

  const handleContinue = async (locationIdOverride?: string) => {
    // Use override if provided (from selection), otherwise use state (from button click)
    const locationId = locationIdOverride ?? locationIdToContinue;
    if (!locationId || !onLocationSelect) return;

    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await onLocationSelect(locationId);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t("locationPickerScreen.submitError"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { data: user } = useQuery({ ...getUser.keyed() });

  const hasSelection = locationIdToContinue !== undefined;
  const isButtonDisabled = isLoading || isSubmitting || (!hasSelection && !isLoading);

  const { mutate: recordReferralSignupStarted } = useMutation({
    mutationFn: (statelessCode: string) =>
      postIncentivesRecordReferralSignupStarted({
        statelessCode: statelessCode,
        personId: user?.id as string,
        platformId: user?.platform?.id as string,
      }),
    onSuccess: () => {
      console.log("Referral signup started");
    },
  });

  useEffect(() => {
    const statelessCode = storage.getItem("statelessReferralCode");

    if (statelessCode && user?.id && user?.platform?.id) {
      recordReferralSignupStarted(statelessCode);
    }
  }, [user]);

  return (
    <div className={`flex w-full flex-col gap-[36px] ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? <div className="flex w-full items-center justify-center">{prefix}</div> : null}

      <div className="flex flex-col gap-[24px]">
        {/* Header */}
        <OnboardingHeaderV3 title={t("locationPickerScreen.title")} align="left" />

        {/* Loading state */}
        {isLoading && (
          <div className="text-center">
            <div className="text-sm text-secondary">
              {loadingMessage ?? t("locationPickerScreen.loadingLocations")}
            </div>
          </div>
        )}

        {/* Location options */}
        {!isLoading && locations && locations.length > 0 && (
          <LocationListPickerV3
            key={chosenPickerKey ?? suggestionPickerKey}
            items={cityGroups}
            multiSelect={false}
            onSelectionChange={handleSelectionChange}
            initialSelectedIds={
              selectedLocation
                ? [selectedLocation]
                : suggestedLocationId
                  ? [suggestedLocationId]
                  : []
            }
            initialCity={suggestedCity}
          />
        )}

        {/* No locations available */}
        {!isLoading && (!locations || locations.length === 0) && (
          <div className="text-center">
            <div className="text-sm text-secondary">
              {t("locationPickerScreen.noLocationsAvailable")}
            </div>
          </div>
        )}
      </div>

      {/* Submit Error */}
      {submitError && <div className="text-center text-sm text-red">{submitError}</div>}

      {/* Continue Button */}
      <Button fullWidth disabled={isButtonDisabled} onClick={() => handleContinue()}>
        {isSubmitting ? t("common.loading") : t("common.continue")}
      </Button>

      {/* Find a practice near you - link to platform picker */}
      {!hideFindNearby && (
        <div className="text-center">
          <TextButton
            onClick={onFindPracticeClick || (() => navigate("/signin/platform-search"))}
            xsText
            className="mx-auto"
          >
            {t("locationPickerScreen.findPracticeNearYou")}
          </TextButton>
        </div>
      )}

      {/* Optional suffix slot */}
      {suffix ? <div className="w-full">{suffix}</div> : null}
    </div>
  );
}
